import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const DEFAULT_HIGHLIGHT_COLORS = ['#fef08a', '#bbf7d0', '#e9d5ff'];

const normalizeHighlightColors = (settings = {}) => {
  if (Array.isArray(settings.highlightColors) && settings.highlightColors.length >= 3) {
    return settings.highlightColors.slice(0, 3);
  }

  if (settings.highlightColor) {
    return [settings.highlightColor, DEFAULT_HIGHLIGHT_COLORS[1], DEFAULT_HIGHLIGHT_COLORS[2]];
  }

  return DEFAULT_HIGHLIGHT_COLORS;
};

// Async thunk to fetch user settings from the server
export const fetchUserSettings = createAsyncThunk(
  'settings/fetchUserSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/users/settings', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.error || 'Failed to fetch settings');
      }
      const data = await response.json();
      return data.settings; // Assuming API response has a `settings` object
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update user settings on the server
export const updateUserSettings = createAsyncThunk(
    "settings/updateUserSettings",
    async (settings, { rejectWithValue }) => {
      try {
        const response = await fetch("/api/users/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(settings),
        });
        if (!response.ok) {
          const errorData = await response.json();
          return rejectWithValue(errorData.error || "Failed to update settings");
        }
        const data = await response.json();
        return data.settings;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );
  

  const initialState = {
    defaultVersion: 'ESV', // Default Bible version
    defaultLanguage: 'en', // Default UI + scripture language
    highlightColor: DEFAULT_HIGHLIGHT_COLORS[0],
    highlightColors: DEFAULT_HIGHLIGHT_COLORS,
    showInterlinear: false,
    loading: false, // Tracks loading state
    error: null, // Tracks error state
    isLoaded: false, // Tracks if settings have been fetched
  };
  
  const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
      setDefaultVersion: (state, action) => {
        state.defaultVersion = action.payload;
      },
      setHighlightColor: (state, action) => {
        state.highlightColor = action.payload;
        state.highlightColors[0] = action.payload;
      },
      resetSettings: (state) => {
        // Reset to initial state
        return { ...initialState };
      },
    },
    extraReducers: (builder) => {
      // Handle fetchUserSettings
      builder.addCase(fetchUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      });
      builder.addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoaded = true; // Mark settings as fetched
        state.defaultVersion = action.payload.defaultVersion || 'ESV';
        state.defaultLanguage = action.payload.defaultLanguage || 'en';
        state.highlightColors = normalizeHighlightColors(action.payload);
        state.highlightColor = state.highlightColors[0];
        state.showInterlinear = Boolean(action.payload.showInterlinear);
      });
      builder.addCase(fetchUserSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to fetch settings.';
      });
  
      // Handle updateUserSettings
      builder.addCase(updateUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      });
      builder.addCase(updateUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.defaultVersion = action.payload.defaultVersion || state.defaultVersion;
        state.defaultLanguage = action.payload.defaultLanguage || state.defaultLanguage;
        state.highlightColors = normalizeHighlightColors(action.payload);
        state.highlightColor = state.highlightColors[0];
        state.showInterlinear = typeof action.payload.showInterlinear === 'boolean'
          ? action.payload.showInterlinear
          : state.showInterlinear;
      });
      builder.addCase(updateUserSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to update settings.';
      });
    },
  });
  
  export const { setDefaultVersion, setHighlightColor, resetSettings } = settingsSlice.actions;
  export default settingsSlice.reducer;
  
